import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard, CheckCircle, AlertTriangle, Clock, ArrowLeft,
  DollarSign, Users, Zap, HardDrive, Package, ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_DISPLAY = {
  trialing: { label: 'Free Trial', color: '#9F7AEA', icon: Clock },
  active: { label: 'Active', color: '#38A169', icon: CheckCircle },
  past_due: { label: 'Past Due', color: '#E53E3E', icon: AlertTriangle },
  canceled: { label: 'Canceled', color: '#718096', icon: AlertTriangle },
  expired: { label: 'Expired', color: '#A0AEC0', icon: AlertTriangle }
};

export default function BillingPortal() {
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const tenantId = localStorage.getItem('tenant_id');

  useEffect(() => {
    if (tenantId) fetchBilling();
    else { setLoading(false); }
  }, [tenantId]);

  const fetchBilling = async () => {
    try {
      const res = await axios.get(`${API}/billing/tenant/${tenantId}`);
      setBilling(res.data);
    } catch { toast.error('Failed to load billing info'); }
    finally { setLoading(false); }
  };

  if (loading) {
    return <div className="billing-portal"><div className="saas-loading">Loading billing info...</div></div>;
  }

  if (!tenantId || !billing) {
    return (
      <div className="billing-portal">
        <div className="billing-empty">
          <h2>No Active Subscription</h2>
          <p>Create a business to get started with billing.</p>
          <button onClick={() => navigate('/admin/create-client')} className="checkout-btn">
            <CreditCard size={18} /> Create Business
          </button>
        </div>
      </div>
    );
  }

  const { tenant, subscription, plan_details } = billing;
  const subStatus = STATUS_DISPLAY[tenant.subscription_status] || STATUS_DISPLAY.trialing;
  const StatusIcon = subStatus.icon;

  return (
    <div className="billing-portal" data-testid="billing-portal">
      <div className="billing-header">
        <button className="back-btn" onClick={() => navigate('/dashboard')}><ArrowLeft size={18} /> Back to Dashboard</button>
        <h1>Billing & Subscription</h1>
        <p>{tenant.name}</p>
      </div>

      <div className="billing-grid">
        {/* Subscription Status */}
        <div className="billing-card status-card">
          <div className="billing-card-header">
            <h3>Subscription Status</h3>
            <span className="status-badge" style={{ background: `${subStatus.color}20`, color: subStatus.color }}>
              <StatusIcon size={14} /> {subStatus.label}
            </span>
          </div>
          <div className="billing-card-body">
            <div className="billing-info-row">
              <span>Plan</span>
              <strong style={{ textTransform: 'capitalize' }}>{tenant.subscription_plan}</strong>
            </div>
            {subscription && (
              <>
                <div className="billing-info-row">
                  <span>Billing Cycle</span>
                  <strong>{subscription.billing_cycle}</strong>
                </div>
                <div className="billing-info-row">
                  <span>Amount</span>
                  <strong>${subscription.amount}/{subscription.billing_cycle === 'annual' ? 'yr' : 'mo'}</strong>
                </div>
                {subscription.current_period_end && (
                  <div className="billing-info-row">
                    <span>Current Period Ends</span>
                    <strong>{new Date(subscription.current_period_end).toLocaleDateString()}</strong>
                  </div>
                )}
                {subscription.provider === 'stripe' && subscription.provider_subscription_id && (
                  <div className="billing-info-row">
                    <span>Stripe ID</span>
                    <code>{subscription.provider_subscription_id}</code>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Plan Details */}
        {plan_details && (
          <div className="billing-card plan-card">
            <div className="billing-card-header">
              <h3>Plan Features</h3>
            </div>
            <div className="billing-card-body">
              <div className="plan-feature"><Users size={16} /> <span>Up to {plan_details.user_limit} users</span></div>
              <div className="plan-feature"><Zap size={16} /> <span>{plan_details.automation_limit} automations</span></div>
              <div className="plan-feature"><HardDrive size={16} /> <span>{plan_details.storage_limit_mb >= 1000 ? `${plan_details.storage_limit_mb / 1000}GB` : `${plan_details.storage_limit_mb}MB`} storage</span></div>
              <div className="plan-feature"><Package size={16} /> <span>{plan_details.modules?.length || 0} modules</span></div>
              <div className="plan-modules-list">
                {plan_details.modules?.map(m => <span key={m} className="module-tag">{m.replace('_', ' ')}</span>)}
              </div>
            </div>
          </div>
        )}

        {/* Payment History */}
        <div className="billing-card history-card">
          <div className="billing-card-header">
            <h3>Payment History</h3>
          </div>
          <div className="billing-card-body">
            {billing.transactions?.length > 0 ? (
              <div className="payment-history">
                {billing.transactions.map(tx => (
                  <div key={tx.id} className="payment-row">
                    <div className="payment-info">
                      <strong>${tx.amount}</strong>
                      <span>{tx.plan_slug} - {tx.billing_cycle}</span>
                    </div>
                    <div className="payment-meta">
                      <span className={`status-badge ${tx.payment_status}`}>{tx.payment_status}</span>
                      <span className="payment-date">{new Date(tx.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-history">No payment history yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Warning for past_due */}
      {tenant.subscription_status === 'past_due' && (
        <div className="billing-warning" data-testid="past-due-warning">
          <AlertTriangle size={20} />
          <div>
            <strong>Payment Past Due</strong>
            <p>Your last payment failed. Please update your payment method to continue using the platform.</p>
          </div>
        </div>
      )}

      {tenant.subscription_status === 'canceled' && (
        <div className="billing-warning canceled" data-testid="canceled-warning">
          <AlertTriangle size={20} />
          <div>
            <strong>Subscription Canceled</strong>
            <p>Your subscription has been canceled. Resubscribe to regain full access.</p>
          </div>
        </div>
      )}
    </div>
  );
}
